import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChannelFormData, Gateway, GatewayChannel, MpxTenant } from '../types';
import { parseFabricUrl } from '../../../utils/channelUtils';
import { ChannelFlowVisualization } from './ChannelFlowVisualization';
import { formatDateTime, timeAgo } from '../../../utils/stringUtils';
import { apiClient } from '../../../api';

export interface OverviewTabProps {
  channel: {
    config_id: string;
    name: string;
    description?: string;
    listen_address: string;
    route?: string;
    target_endpoint: string;
    fabric_target_name?: string;
    enabled: boolean;
    extension_rules?: {
      rules: Array<{ [key: string]: any }>;
      json_schema?: any;
      [key: string]: any;
    };
    response_extension_rules?: {
      rules: Array<{ [key: string]: any }>;
      json_schema?: any;
      [key: string]: any;
    };
    custom_metadata?: {
      enabled?: boolean;
      payload?: any;
    };
    rule_count: number;
    accept_count: number;
    deny_count: number;
    enforce_extension: boolean;
    enforce_response_extension: boolean;
    gateway_faults: number;
    last_status: string | null;
    latest_payload?: any;
    channel_type?: 'user' | 'onboarding' | 'system' | 'transit';
    protocol?: 'a2a' | 'ap2' | 'mcp' | 'mpx' | 'didcomm';
    last_activity?: string;
  };
  formData: ChannelFormData;
  fullRouteDisplay: string;
  gateways: Gateway[];
  gatewayChannels: GatewayChannel[];
  channelNameCache: Map<string, string>;
  mediators?: Array<{ id: string; name: string; description: string; did: string; status?: string }>;
  mpxTenants?: MpxTenant[];
  handleInputChange: (field: keyof ChannelFormData, value: string | boolean) => void;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  channel,
  formData,
  fullRouteDisplay,
  gateways,
  gatewayChannels,
  channelNameCache,
  mediators = [],
  mpxTenants = [],
  handleInputChange,
}) => {
  const navigate = useNavigate();
  const getFabricDisplayNames = (endpoint: string): { gatewayName: string; channelName: string } => {
    const parsed = parseFabricUrl(endpoint);
    if (!parsed || !parsed.channelId) {
      return { gatewayName: '', channelName: '' };
    }

    const gateway = gateways.find(g => g.id === parsed.gatewayId);
    const gatewayName = gateway?.name || parsed.gatewayId || 'Unknown Gateway';
    
    // First check if we have a stored fabric_target_name in the channel data
    if (channel?.fabric_target_name) {
      return { gatewayName, channelName: channel.fabric_target_name };
    }
    
    // Then check cache
    if (channelNameCache.has(parsed.channelId)) {
      return { gatewayName, channelName: channelNameCache.get(parsed.channelId)! };
    }
    
    // Then try gatewayChannels
    const channelInList = gatewayChannels.find(c => c.config_id === parsed.channelId);
    if (channelInList) {
      return { gatewayName, channelName: channelInList.name };
    }
    
    return { gatewayName, channelName: '' };
  };

  return (
    <>
      {/* Full Route Info */}
      <div className="alert alert-success mb-3" style={{ fontSize: '14px' }}>
        <i className="fas fa-info-circle mr-2"></i><strong>Channel Route:</strong> {fullRouteDisplay}
        <button
          type="button"
          className="btn btn-xs ml-2"
          style={{ padding: '2px 6px', fontSize: '11px', outline: 'none' }}
          onClick={(e) => {
            navigator.clipboard.writeText(fullRouteDisplay);
            const btn = e.currentTarget as HTMLButtonElement;
            const originalHtml = btn.innerHTML;
            btn.innerHTML = '<i class="fas fa-check"></i>';
            setTimeout(() => { btn.innerHTML = originalHtml; }, 2000);
          }}
          title="Copy to clipboard"
        >
          <i className="fas fa-copy"></i>
        </button>
      </div>

      {/* Channel Mapping Info */}
      <div className="alert alert-info mb-3" style={{ fontSize: '14px' }}>
        <div className="mb-2">
          <i className="fas fa-route mr-2"></i>
          <strong>Target: </strong> {channel.target_endpoint.startsWith('fabric://') ? (
            (() => {
              const { gatewayName, channelName } = getFabricDisplayNames(channel.target_endpoint);
              const parsed = parseFabricUrl(channel.target_endpoint);
              return (
                <span title={channel.target_endpoint}>                              
                    {gatewayName}
                    {channelName && <> → {channelName}</>}
                    {!channelName && parsed?.channelId && (
                      <span className="text-muted"> → {parsed.channelId.substring(0, 8)}...</span>
                    )}                            
                </span>
              );
            })()
          ) : (
            <span>{channel.target_endpoint}</span>
          )}
          <button
            type="button"
            className="btn btn-xs ml-2"
            style={{ padding: '2px 6px', fontSize: '11px', outline: 'none' }}
            onClick={(e) => {
              navigator.clipboard.writeText(channel.target_endpoint);
              const btn = e.currentTarget as HTMLButtonElement;
              const originalHtml = btn.innerHTML;
              btn.innerHTML = '<i class="fas fa-check"></i>';
              setTimeout(() => { btn.innerHTML = originalHtml; }, 2000);
            }}
            title="Copy to clipboard"
          >
            <i className="fas fa-copy"></i>
          </button>
        </div>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-info-circle"></i> Basic Information
            </h6>
          </div>
        </div>
        <div className="card-body">
      
      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="channel-edit-name">Channel Name</label>
          <input 
            type="text" 
            className="form-control" 
            id="channel-edit-name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
          />
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="channel-edit-config-id">Channel Id</label>
          <input 
            type="text" 
            className="form-control" 
            id="channel-edit-config-id"
            value={formData.config_id}
            readOnly 
            disabled
          />
        </div>
      </div>
      
      <div className="mb-3">
        <label htmlFor="channel-edit-description">Description</label>
        <input 
          type="text" 
          className="form-control" 
          id="channel-edit-description"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Enter channel description"
        />
      </div>

      {/* Display Extensions (read-only) */}
      {(formData.primary_extension || (formData.supported_extensions && formData.supported_extensions.length > 0)) && (
        <div className="mb-3">
          <label className="font-weight-bold">
            <i className="fas fa-puzzle-piece text-info"></i> Protocol Extensions
          </label>
          <div className="border rounded p-3 bg-light">
            {(() => {
              // Collect all unique extensions
              const extensions = new Set<string>();
              if (formData.primary_extension) extensions.add(formData.primary_extension);
              if (formData.supported_extensions) {
                formData.supported_extensions.forEach(ext => extensions.add(ext));
              }
              
              return Array.from(extensions).map((ext, idx) => (
                <div key={idx} className="mb-2">
                  <i className="fas fa-check-circle text-success mr-2"></i>
                  <code className="text-dark">{ext}</code>
                  {ext.includes('ucp.dev') && (
                    <span className="badge badge-info ml-2">UCP Commerce</span>
                  )}
                  {ext === formData.primary_extension && extensions.size > 1 && (
                    <span className="badge badge-primary ml-2">Primary</span>
                  )}
                </div>
              ));
            })()}
            {formData.enforce_extension && (
              <div className="mt-2 pt-2 border-top">
                <span className="badge badge-warning">
                  <i className="fas fa-shield-alt"></i> Extension Required
                </span>
                <small className="text-muted ml-2">Requests must include configured extension</small>
              </div>
            )}
          </div>
        </div>
      )}
      
      <div className="mb-3">
        <i className="fas fa-clock text-muted me-2"></i><strong> Last Activity: </strong> 
        {channel.last_activity ? (
          <span>
            {timeAgo(channel.last_activity)} <span className="text-muted"> - {formatDateTime(channel.last_activity, true)}</span>
          </span>
        ) : (
          <span className="text-muted">No activity recorded</span>
        )}
      </div>

      <div className="mb-4">
        <div className="form-check">
          <input 
            type="checkbox" 
            className="form-check-input" 
            id="channel-edit-enabled"
            checked={formData.enabled}
            onChange={(e) => handleInputChange('enabled', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="channel-edit-enabled">
            <strong>Channel Enabled</strong> - When unchecked, the channel will be stopped and not loaded at startup
            {!formData.enabled && (
              <span className="badge badge-warning ml-2">
                <i className="fas fa-power-off"></i> DISABLED
              </span>
            )}
          </label>
        </div>
        
        <div className="form-check mt-2">
          <input 
            type="checkbox" 
            className="form-check-input" 
            id="channel-edit-publish-to-did"
            checked={formData.publish_to_did_document || false}
            onChange={(e) => handleInputChange('publish_to_did_document', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="channel-edit-publish-to-did">
            <strong>Publish to DID Document</strong> - When checked, this channel will be published as a service in the Gateway's DID document
            {formData.publish_to_did_document && (
              <span className="badge badge-info ml-2">
                <i className="fas fa-globe"></i> PUBLISHED
              </span>
            )}
          </label>
        </div>
      </div>

      <div className="mb-3">
        {channel.channel_type === 'onboarding' && (
          <span className="badge badge-info mr-2">
            <i className="fas fa-user-plus"></i> ONBOARDING
          </span>
        )}
        {channel.channel_type === 'system' && (
          <span className="badge badge-secondary mr-2">
            <i className="fas fa-cog"></i> SYSTEM
          </span>
        )}
        {channel.channel_type === 'transit' && (
          <span className="badge badge-warning mr-2">
            <i className="fas fa-exchange-alt"></i> TRANSIT
          </span>
        )}
      </div>
        </div>
      </div>

      {/* MPX Protocol Configuration */}
      {formData.protocol === 'mpx' && (
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-light">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-project-diagram"></i> MPX Protocol Configuration
            </h6>
          </div>
          <div className="card-body">
            {/* MPX Tenant (Read-only) */}
            {formData.mpx_tenant_id && (
              <div className="mb-3">
                <label className="form-label fw-bold">
                  MPX Tenant
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={(() => {
                    const tenant = mpxTenants?.find(t => t.id === formData.mpx_tenant_id);
                    return tenant ? `${tenant.name} (${tenant.did})` : formData.mpx_tenant_id;
                  })()}
                  readOnly
                  disabled
                />
                <small className="form-text text-muted">
                  The MPX Control Plane tenant used for this channel
                </small>
              </div>
            )}

            {/* MPX Control Plane (Read-only) */}
            {formData.mpx_tenant_id && (
              <div className="mb-3">
                <label className="form-label fw-bold">
                  MPX Control Plane
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={(() => {
                    const tenant = mpxTenants?.find(t => t.id === formData.mpx_tenant_id);
                    return tenant ? tenant.api_endpoint : 'Not available';
                  })()}
                  readOnly
                  disabled
                />
                <small className="form-text text-muted">
                  The MPX Control Plane API endpoint
                </small>
              </div>
            )}

            {/* Connection Passphrase (Read-only) */}
            {formData.mpx_custom_phrase && (
              <div className="mb-3">
                <label className="form-label fw-bold">
                  Connection Passphrase
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.mpx_custom_phrase}
                  readOnly
                  disabled
                />
                <small className="form-text text-muted">
                  The custom passphrase used for this MPX offer connection
                </small>
              </div>
            )}

            {/* Mediator (Read-only) */}
            <div className="mb-3">
              <label className="form-label fw-bold">
                Mediator
              </label>
              <input
                type="text"
                className="form-control"
                value={formData.mpx_mediator_id ? (() => {
                  const mediator = mediators?.find(m => m.id === formData.mpx_mediator_id);
                  return mediator ? `${mediator.name || 'Unnamed Mediator'} - ${mediator.did}` : formData.mpx_mediator_id;
                })() : 'Not configured'}
                readOnly
                disabled
              />
              <small className="form-text text-muted">
                The DIDComm v2.1 mediator used for this MPX channel
              </small>
            </div>

            {/* Device Token (Read-only) */}
            {formData.mpx_device_token && (
              <div className="mb-3">
                <label className="form-label fw-bold">
                  Device Token
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.mpx_device_token}
                  readOnly
                  disabled
                />
                <small className="form-text text-muted">
                  The device token registered for DIDComm notifications (automatically generated)
                </small>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Channel Flow Visualization */}
      <ChannelFlowVisualization
        channel={{
          name: channel.name,
          config_id: channel.config_id,
          description: channel.description,
          listen_address: channel.listen_address,
          target_endpoint: channel.target_endpoint,
          fabric_target_name: channel.fabric_target_name,
          protocol: channel.protocol,
          channel_type: channel.channel_type,
          rate_limit: formData.rate_limit,
          enforce_extension: formData.enforce_extension,
          enforce_response_extension: formData.enforce_response_extension,
          custom_metadata: formData.custom_metadata,
          response_custom_metadata: formData.response_custom_metadata,
          timeout: formData.timeout,
          retry: formData.retry,
          circuit_breaker: formData.circuit_breaker,
          mirror: formData.mirror,
          opa_policy_config: formData.opa_policy_config,
          mcp_tool_policies_config: formData.mcp_tool_policies_config,
        }}
        gateways={gateways}
        gatewayChannels={gatewayChannels}
        channelNameCache={channelNameCache}
        navigate={navigate}
      />
    </>
  );
};

export default OverviewTab;
